import React from 'react'
import {useSignedMediaUrl} from 'src/app/queries/signedMediaUrl'

type Props = {
  /** Stored upload path as saved by the media manager, e.g. "uploads/Logo/brand.png". */
  path?: string | null
  className?: string
  alt?: string
  'data-cy'?: string
}

/**
 * An <img> for a file the media manager stored.
 *
 * Files under /uploads are gated behind an expiring signature since the VAPT remediation, so the
 * old `${imageBaseUrl}/${storedValue}` pattern renders a broken image. This asks the backend for
 * a signed link first — see src/app/queries/signedMediaUrl.ts for why the path, and not the URL,
 * is what gets persisted.
 *
 * Exists as a component rather than being inlined because these previews live inside Formik
 * render props, where a hook cannot be called directly.
 *
 * Renders nothing until the link arrives. That is deliberate: an <img> pointed at an unsigned
 * upload path shows a broken-image icon, which reads as "the file is gone" rather than "the link
 * is still being minted".
 */
const SignedImage: React.FC<Props> = ({path, className, alt = '', ...rest}) => {
  const url = useSignedMediaUrl(path)

  if (!url) {
    return null
  }

  return <img className={className} src={url} alt={alt} {...rest} />
}

export default SignedImage
